import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Undo2 } from 'lucide-react';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
	DIALOG_CANCEL_CLASS,
	DIALOG_CONTENT_CLASS,
	DIALOG_DANGER_CLASS,
	DIALOG_DESCRIPTION_CLASS,
	DIALOG_FOOTER_CLASS,
	DIALOG_HEADER_CLASS,
	DIALOG_TITLE_CLASS
} from '@/components/shared/dialog-styles';
import { toast } from '@/components/ui/sonner';
import { useBusinessUnit } from '@/hooks/use-business-units';
import { useRollBackAutopilot } from './use-ticket-rules';

/** The "Disable playbooks" confirmation (a roll back under the hood). */
export function useRollBackDialog() {
	const navigate = useNavigate();
	const rollBack = useRollBackAutopilot();
	const { buId, businessUnits } = useBusinessUnit();
	const brandName = businessUnits.find((unit) => unit.id === buId)?.name ?? 'this brand';
	const [open, setOpen] = useState(false);

	const confirm = async () => {
		try {
			await rollBack.mutateAsync();
			toast.success(`${brandName} is back on its previous settings`);
			navigate('/settings/brand');
		} catch (error) {
			console.error('[AutopilotMigration.rollBack] failed', { buId, error });
			toast.error("Couldn't confirm playbooks were disabled. Reload to check whether they are still on before trying again.");
		}
	};

	const dialog = (
		<AlertDialog open={open} onOpenChange={setOpen}>
			<AlertDialogContent className={DIALOG_CONTENT_CLASS}>
				<AlertDialogHeader className={DIALOG_HEADER_CLASS}>
					<Undo2 className='size-7 text-red-500' />
					<AlertDialogTitle className={DIALOG_TITLE_CLASS}>Disable playbooks for {brandName}?</AlertDialogTitle>
					<AlertDialogDescription className={DIALOG_DESCRIPTION_CLASS}>
						Playbooks switch off and Kim goes back to this brand&apos;s previous settings, exactly as they were before playbooks were switched on.
						Your playbooks are kept but don&apos;t run, so you can switch playbooks on again later.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter className={DIALOG_FOOTER_CLASS}>
					<AlertDialogCancel className={DIALOG_CANCEL_CLASS}>Cancel</AlertDialogCancel>
					<AlertDialogAction onClick={() => void confirm()} className={DIALOG_DANGER_CLASS}>
						Disable
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);

	return { open: () => setOpen(true), pending: rollBack.isPending, dialog };
}
